import { z } from "zod";
import type {
  AlteracaoDePlano,
  ConfirmacaoNecessaria,
  Plano,
  PlanoNovo,
  PoliticaDeExcedente,
} from "@/api/contrato";
import { FalhaDaApi } from "@/api/problema";
import { quantidade } from "@/formato";

/*
 * O plano no painel: rótulos, o formulário e a validação que espelha a API.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A VALIDAÇÃO DAQUI ESPELHA A DA API, E NÃO A SUBSTITUI.                   │
 * │                                                                          │
 * │ Ela existe para o operador ver "não pode ser negativo" no campo antes da │
 * │ viagem — e para o corpo sair com os tipos certos: centavos inteiros,     │
 * │ `null` onde o vazio significa "sem limite". Quem decide é o servidor; a  │
 * │ recusa dele volta com `campo`, e a tela marca o mesmo campo.             │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

export const POLITICAS: readonly (readonly [PoliticaDeExcedente, string])[] = [
  ["overage", "Cobra excedente"],
  ["hard_block", "Bloqueia"],
  ["soft_warn", "Só avisa"],
];

export function rotuloDaPolitica(politica: string | undefined): string {
  if (!politica) return "—";
  // O conjunto é do banco (enum `politica_excedente`). Um valor novo aparece
  // cru, e não some: a tela não pode esconder a regra que está valendo.
  return POLITICAS.find(([valor]) => valor === politica)?.[1] ?? politica;
}

/**
 * A franquia para leitura. `null` é "sem limite", e nunca zero: zero é a
 * franquia zero do "pague por uso", em que todo boleto é excedente.
 */
export function franquia(limite: number | null | undefined): string {
  if (limite === null || limite === undefined) return "sem limite";
  return `${quantidade(limite)} boletos`;
}

/* ── Formulário ──────────────────────────────────────────────────────────── */

/*
 * As chaves do formulário SÃO os nomes da API. A recusa do servidor aponta o
 * campo por esse nome (`campo` do problema), e uma tradução no meio seria um
 * mapa para esquecer de atualizar quando um campo novo entrar.
 *
 * Tudo é texto enquanto o operador digita; o tipo certo sai da validação.
 */
export interface ValoresDoPlano {
  nome: string;
  descricao: string;
  preco_base_centavos: string;
  limite_mensal: string;
  politica_excedente: PoliticaDeExcedente;
  preco_excedente_centavos: string;
  teto_excedente: string;
  limite_rps: string;
}

export type CampoDoPlano = keyof ValoresDoPlano;
export type ErrosDoPlano = Partial<Record<CampoDoPlano, string | undefined>>;

export const VALORES_INICIAIS: ValoresDoPlano = {
  nome: "",
  descricao: "",
  preco_base_centavos: "",
  limite_mensal: "",
  politica_excedente: "overage",
  preco_excedente_centavos: "",
  teto_excedente: "",
  limite_rps: "",
};

/** O plano gravado, como o formulário o mostra para edição. */
export function valoresDe(plano: Plano): ValoresDoPlano {
  return {
    nome: plano.nome,
    descricao: plano.descricao ?? "",
    preco_base_centavos: reaisDe(plano.preco_base_centavos),
    limite_mensal: plano.limite_mensal === null ? "" : String(plano.limite_mensal),
    politica_excedente: plano.politica_excedente,
    preco_excedente_centavos: reaisDe(plano.preco_excedente_centavos),
    teto_excedente: plano.teto_excedente === null ? "" : String(plano.teto_excedente),
    limite_rps: plano.limite_rps === null ? "" : String(plano.limite_rps),
  };
}

/** `2990` → `"29,90"`. Sem símbolo: o campo já diz que é em reais. */
function reaisDe(centavos: number): string {
  const sinal = centavos < 0 ? "-" : "";
  const absoluto = Math.abs(centavos);
  return `${sinal}${Math.floor(absoluto / 100)},${String(absoluto % 100).padStart(2, "0")}`;
}

/**
 * `"1.234,56"`, `"29,90"`, `"29.90"` ou `"29"` → centavos inteiros.
 *
 * Sem `parseFloat` no caminho: dinheiro é inteiro em centavos em todo o
 * produto, e `Math.round(29.9 * 100)` é a mesma aposta em ponto flutuante que o
 * resto do código se recusa a fazer. A conta é feita sobre os dígitos.
 */
export function centavosDe(texto: string): number | null {
  let normalizado = texto.trim().replace(/\s/g, "");
  if (normalizado.includes(",")) {
    normalizado = normalizado.replace(/\./g, "").replace(",", ".");
  } else if (!/^-?\d+\.\d{1,2}$/.test(normalizado)) {
    // Ponto sem casa decimal de dinheiro é separador de milhar: "1.000".
    normalizado = normalizado.replace(/\./g, "");
  }

  const partes = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(normalizado);
  if (partes === null) return null;

  const [, sinal, inteiros = "0", decimais = ""] = partes;
  const valor = Number(inteiros) * 100 + Number(decimais.padEnd(2, "0"));
  return sinal === "-" ? -valor : valor;
}

const NEGATIVO = "Não pode ser negativo.";

/*
 * Os tetos de cima também são os da API: franquia, teto e `limite_rps` são
 * `integer` no banco (até 2³¹−1), e os centavos são `bigint`, que o `number`
 * do navegador só representa exato até `MAX_SAFE_INTEGER`.
 */
const MAXIMO_INTEIRO = 2_147_483_647;
const ACIMA_DO_MAXIMO = "Valor acima do máximo.";

function inteiroOpcional(minimo: number, abaixoDoMinimo: string) {
  return z.string().transform((texto, contexto) => {
    const valor = texto.trim();
    if (valor === "") return null;
    if (!/^-?\d+$/.test(valor)) {
      contexto.addIssue({ code: "custom", message: "Use um número inteiro." });
      return z.NEVER;
    }
    const numero = Number(valor);
    if (numero < minimo) {
      contexto.addIssue({ code: "custom", message: abaixoDoMinimo });
      return z.NEVER;
    }
    if (numero > MAXIMO_INTEIRO) {
      contexto.addIssue({ code: "custom", message: ACIMA_DO_MAXIMO });
      return z.NEVER;
    }
    return numero;
  });
}

const reais = z.string().transform((texto, contexto) => {
  if (texto.trim() === "") return 0;
  const centavos = centavosDe(texto);
  if (centavos === null) {
    contexto.addIssue({ code: "custom", message: "Use um valor como 29,90." });
    return z.NEVER;
  }
  if (centavos < 0) {
    contexto.addIssue({ code: "custom", message: NEGATIVO });
    return z.NEVER;
  }
  if (centavos > Number.MAX_SAFE_INTEGER) {
    contexto.addIssue({ code: "custom", message: ACIMA_DO_MAXIMO });
    return z.NEVER;
  }
  return centavos;
});

/*
 * Os mínimos são os da API: franquia e teto aceitam zero (franquia zero é o
 * "pague por uso"; teto zero é "nenhum excedente"), e `limite_rps` começa em 1
 * — zero requisições por segundo seria uma aplicação que não emite.
 */
const ESQUEMA = z.object({
  nome: z.string().trim().min(1, "Informe o nome."),
  descricao: z.string().transform((texto) => (texto.trim() === "" ? null : texto.trim())),
  preco_base_centavos: reais,
  limite_mensal: inteiroOpcional(0, NEGATIVO),
  politica_excedente: z.enum(["hard_block", "overage", "soft_warn"]),
  preco_excedente_centavos: reais,
  teto_excedente: inteiroOpcional(0, NEGATIVO),
  limite_rps: inteiroOpcional(1, "Mínimo de 1."),
});

export type PlanoValidado = z.output<typeof ESQUEMA>;

export type ResultadoDaValidacao =
  | { ok: true; plano: PlanoValidado }
  | { ok: false; erros: ErrosDoPlano };

export function validarPlano(valores: ValoresDoPlano): ResultadoDaValidacao {
  const resultado = ESQUEMA.safeParse(valores);
  if (resultado.success) return { ok: true, plano: resultado.data };

  const erros: ErrosDoPlano = {};
  for (const problema of resultado.error.issues) {
    const campo = problema.path[0];
    if (typeof campo === "string" && campo in valores && erros[campo as CampoDoPlano] === undefined) {
      erros[campo as CampoDoPlano] = problema.message;
    }
  }
  return { ok: false, erros };
}

/**
 * Sob `hard_block` não existe excedente: a emissão acima da franquia é
 * recusada. O formulário esconde preço e teto de excedente nessa política, e o
 * corpo não os leva — na edição, o que estava gravado fica como estava.
 */
export function usaExcedente(politica: PoliticaDeExcedente): boolean {
  return politica !== "hard_block";
}

/** O corpo da criação: só o que foi preenchido, porque ausente tem significado. */
export function corpoDaCriacao(plano: PlanoValidado): PlanoNovo {
  const comExcedente = usaExcedente(plano.politica_excedente);

  return {
    nome: plano.nome,
    politica_excedente: plano.politica_excedente,
    preco_base_centavos: plano.preco_base_centavos,
    ...(plano.descricao !== null && { descricao: plano.descricao }),
    // Franquia ausente é SEM LIMITE — e é por isso que o vazio não vira zero.
    ...(plano.limite_mensal !== null && { limite_mensal: plano.limite_mensal }),
    ...(comExcedente && { preco_excedente_centavos: plano.preco_excedente_centavos }),
    ...(comExcedente && plano.teto_excedente !== null && { teto_excedente: plano.teto_excedente }),
    ...(plano.limite_rps !== null && { limite_rps: plano.limite_rps }),
  };
}

/** Os campos que não mudam cobrança nem quota — os únicos sem confirmação. */
const NAO_COMERCIAIS = new Set<keyof AlteracaoDePlano>(["nome", "descricao", "disponivel"]);

/**
 * O corpo da edição: só o que MUDOU.
 *
 * Mandar o plano inteiro faria uma correção de nome cair na confirmação de
 * contratos vigentes, porque o servidor veria os campos comerciais no pedido.
 */
export function corpoDaEdicao(antes: Plano, depois: PlanoValidado): AlteracaoDePlano {
  const corpo: AlteracaoDePlano = {};

  if (depois.nome !== antes.nome) corpo.nome = depois.nome;
  if (depois.descricao !== antes.descricao) corpo.descricao = depois.descricao;
  if (depois.preco_base_centavos !== antes.preco_base_centavos) {
    corpo.preco_base_centavos = depois.preco_base_centavos;
  }
  if (depois.limite_mensal !== antes.limite_mensal) corpo.limite_mensal = depois.limite_mensal;
  if (depois.politica_excedente !== antes.politica_excedente) {
    corpo.politica_excedente = depois.politica_excedente;
  }
  if (usaExcedente(depois.politica_excedente)) {
    if (depois.preco_excedente_centavos !== antes.preco_excedente_centavos) {
      corpo.preco_excedente_centavos = depois.preco_excedente_centavos;
    }
    if (depois.teto_excedente !== antes.teto_excedente) corpo.teto_excedente = depois.teto_excedente;
  }
  if (depois.limite_rps !== antes.limite_rps) corpo.limite_rps = depois.limite_rps;

  return corpo;
}

export function mudaCampoComercial(corpo: AlteracaoDePlano): boolean {
  return (Object.keys(corpo) as (keyof AlteracaoDePlano)[]).some(
    (campo) => !NAO_COMERCIAIS.has(campo),
  );
}

/** O `409 confirmacao_necessaria`, quando é ele. */
export function confirmacaoNecessaria(falha: unknown): ConfirmacaoNecessaria | null {
  if (!(falha instanceof FalhaDaApi) || falha.codigo !== "confirmacao_necessaria") return null;
  const problema = falha.problema as Partial<ConfirmacaoNecessaria> | null;
  if (typeof problema?.contratos_vigentes !== "number") return null;
  return {
    ...(problema as ConfirmacaoNecessaria),
    marca_dos_vigentes: problema.marca_dos_vigentes ?? "",
    contratos: problema.contratos ?? [],
  };
}

/**
 * O `409 plano_faturado`: o plano já gerou fatura e o pedido muda campo
 * comercial (change `add-faturamento`). Não há confirmação que o libere — a
 * saída é plano novo e troca dos contratos, e é o que a recusa diz.
 */
export function planoFaturado(falha: unknown): boolean {
  return falha instanceof FalhaDaApi && falha.codigo === "plano_faturado";
}

/** O campo que a recusa da API aponta, quando é um campo deste formulário. */
export function campoRecusado(falha: unknown): CampoDoPlano | null {
  if (!(falha instanceof FalhaDaApi)) return null;
  const campo = falha.problema?.campo;
  return campo !== undefined && campo in VALORES_INICIAIS ? (campo as CampoDoPlano) : null;
}
